import type { MRT_ColumnOrderState, MRT_ColumnSizingState, MRT_Updater } from "material-react-table";
import { useContext, useEffect, useRef, useState } from "react";
import { TableColumnPreferencesContext } from "_shared/_views/tables/table-column-preferences-context";

/**
 * The column widths a reader dragged and the column order they chose, kept in the table's current view
 * on their account (#6714), beside the Show/Hide columns choice (`usePersistedColumnVisibility`).
 *
 * Before this, both lived in the table alone: leave the page and every dragged column went back to its
 * fitted width.
 *
 * Which layout the table shows:
 * - until the reader changes something here, the current view's, as the server last answered it, less
 *   the columns the table no longer has;
 * - once they have, their change, whatever arrives after it.
 *
 * Nothing is saved when the table opens, nor when MRT itself puts a column in or out of its order (a
 * row actions column that appears with the first row). Only a drag or a reorder is the reader's.
 */

/** What a display column MRT draws for itself is called: the select boxes, the row actions. */
const DISPLAY_COLUMN_PREFIX = "mrt-row-";

function isDataColumn(columnId: string): boolean {
  return !columnId.startsWith(DISPLAY_COLUMN_PREFIX);
}

const NO_SIZING: MRT_ColumnSizingState = Object.freeze({}) as MRT_ColumnSizingState;
const NO_ORDER: readonly string[] = Object.freeze([]);

/** The stored widths of the columns the table still has. */
export function keepKnownSizing(
  sizing: Readonly<Record<string, number>> | undefined,
  columnIds: ReadonlySet<string>,
): MRT_ColumnSizingState {
  const known = Object.entries(sizing ?? {}).filter(([columnId]) => columnIds.has(columnId));
  return known.length === 0 ? NO_SIZING : Object.fromEntries(known);
}

/** The stored order of the columns the table still has, its own display columns left out. */
export function keepKnownOrder(order: readonly string[] | undefined, columnIds: ReadonlySet<string>): readonly string[] {
  const known = (order ?? []).filter((columnId) => isDataColumn(columnId) && columnIds.has(columnId));
  return known.length === 0 ? NO_ORDER : known;
}

export function sameSizing(a: Readonly<Record<string, number>>, b: Readonly<Record<string, number>>): boolean {
  const aKeys = Object.keys(a);
  return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key]);
}

/**
 * Whether `next` orders the data columns `shown` has differently. A column put in or taken out, as
 * MRT does when a display column appears, moves none of the others, and is not a reorder.
 */
export function isReorder(shown: readonly string[], next: readonly string[]): boolean {
  const inNext = new Set(next);
  const inShown = new Set(shown);
  const before = shown.filter((columnId) => isDataColumn(columnId) && inNext.has(columnId));
  const after = next.filter((columnId) => isDataColumn(columnId) && inShown.has(columnId));
  return before.some((columnId, index) => after[index] !== columnId);
}

type HeldLayout = {
  /** The reader's widths, once they have dragged one here. */
  sizing?: MRT_ColumnSizingState;
  /** The reader's whole order, MRT's display columns included, once they have reordered here. */
  order?: MRT_ColumnOrderState;
  /** The table's view revision when the reader chose: a view opened since then outranks the choice (#6717). */
  revision?: number;
};

export type PersistedColumnLayout = {
  /** The reader's own widths: what lies over the widths the table gives its columns. */
  sizing: MRT_ColumnSizingState;
  /** Takes the reader's widths. A value that is what the table already shows is not a change. */
  setSizing: (updater: MRT_Updater<MRT_ColumnSizingState>) => void;
  /** The reader's order once they reordered here, else the stored one: data columns alone, maybe none. */
  order: readonly string[];
  /** Whether `order` is the whole order the reader made here, MRT's display columns included. */
  orderIsWhole: boolean;
  /** Takes an order MRT hands back. `shown` is the order the table drew, to tell a reorder from MRT's own. */
  setOrder: (updater: MRT_Updater<MRT_ColumnOrderState>, shown: MRT_ColumnOrderState) => void;
  /** Whether the reader has dragged a width here. */
  sizingChosen: boolean;
  /** Whether the reader has reordered here. */
  orderChosen: boolean;
  /** The table's view revision: a view opened since is a new start for what was saved (#6717). */
  revision: number;
};

/**
 * The reader's widths and order for one table. Without a `tableKey` nothing is read or saved, and the
 * widths and order are this table's alone, as they were before.
 *
 * `columnIds` are the ids of the table's own columns; a stored width or place of any other is dropped.
 * Saving is {@link useSaveColumnLayout}'s, called once the table exists, because a drag is saved when
 * it ends and only the table knows when that is.
 */
export function usePersistedColumnLayout(
  tableKey: string | undefined,
  columnIds: ReadonlySet<string>,
): PersistedColumnLayout {
  const preferences = useContext(TableColumnPreferencesContext);
  const stored = tableKey ? preferences?.savedLayouts?.get(tableKey) : undefined;
  const revision = tableKey ? (preferences?.viewRevisions?.get(tableKey) ?? 0) : 0;
  const [heldAt, setHeld] = useState<HeldLayout>({ revision });
  // A view opened from the Views menu since the reader's last change here: its layout is the stored one.
  const held: HeldLayout = heldAt.revision === revision ? heldAt : {};

  const sizing = held.sizing ?? keepKnownSizing(stored?.columnSizing, columnIds);
  const order = held.order ?? keepKnownOrder(stored?.columnOrder, columnIds);

  // Plain functions: MRT's options are built afresh on every render, so nothing holds on to them.
  const setSizing = (updater: MRT_Updater<MRT_ColumnSizingState>) => {
    const next = typeof updater === "function" ? updater(sizing) : updater;
    if (sameSizing(next, sizing)) return;
    setHeld((previous) => ({ ...current(previous, revision), sizing: next }));
  };

  const setOrder = (updater: MRT_Updater<MRT_ColumnOrderState>, shown: MRT_ColumnOrderState) => {
    const next = typeof updater === "function" ? updater(shown) : updater;
    setHeld((stale) => {
      const previous = current(stale, revision);
      // The order the reader made, kept whole as MRT moves its own columns in and out of it.
      if (previous.order) return { ...previous, order: next };
      // MRT putting a column in or out of an order nobody chose: the table works it out again.
      if (!isReorder(shown, next)) return stale;
      return { ...previous, order: next };
    });
  };

  return {
    sizing,
    setSizing,
    order,
    orderIsWhole: held.order !== undefined,
    setOrder,
    sizingChosen: held.sizing !== undefined,
    orderChosen: held.order !== undefined,
    revision,
  };
}

/** What the reader chose here, unless a view was opened since: then nothing, at this revision. */
function current(held: HeldLayout, revision: number): HeldLayout {
  return held.revision === revision ? held : { revision };
}

/**
 * Saves what the reader changed in {@link usePersistedColumnLayout}'s layout, each part on its own so
 * a part they did not touch keeps the stored one. A width is saved once its drag ends: MRT resizes on
 * every pointer move.
 */
export function useSaveColumnLayout(
  tableKey: string | undefined,
  layout: PersistedColumnLayout,
  isResizing: boolean,
): void {
  const saveLayout = useContext(TableColumnPreferencesContext)?.saveLayout;
  const savedSizing = useRef<MRT_ColumnSizingState | undefined>(undefined);
  const savedOrder = useRef<readonly string[] | undefined>(undefined);
  const savedAt = useRef(layout.revision);
  const { sizing, sizingChosen, order, orderChosen, revision } = layout;

  // What was saved before a view was opened is not what the opened view keeps: a reorder back to the
  // order saved for the view before would otherwise read as no change, and never be saved (#6717 review).
  function forgetSavedBefore(at: number): void {
    if (savedAt.current === at) return;
    savedAt.current = at;
    savedSizing.current = undefined;
    savedOrder.current = undefined;
  }

  useEffect(() => {
    forgetSavedBefore(revision);
    if (!tableKey || !sizingChosen || isResizing || savedSizing.current === sizing) return;
    savedSizing.current = sizing;
    saveLayout?.(tableKey, { columnSizing: sizing });
  }, [isResizing, revision, saveLayout, sizing, sizingChosen, tableKey]);

  useEffect(() => {
    forgetSavedBefore(revision);
    if (!tableKey || !orderChosen) return;
    const dataOrder = order.filter(isDataColumn);
    if (savedOrder.current && !isReorder(savedOrder.current, dataOrder)) return;
    savedOrder.current = dataOrder;
    saveLayout?.(tableKey, { columnOrder: dataOrder });
  }, [order, orderChosen, revision, saveLayout, tableKey]);
}
